import Vue from 'vue';
import values from 'lodash/values';
import { useStarsStore } from '@/stores/stars.store';
import { fetchPages, fetchPage, updatePage, createPage, deletePage } from './pages.utils';
import { createFrozenMap, appendKindIfNotProvided } from '@/vuex/utils';
import { RECENTLY_SELECTED_HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';
import { formatCrossAppDisplayName } from '@/vuex/modules/apps/apps.utils.js';

let loadAllPromise;

const pages = {
    namespaced: true,
    state: {
        error: {},
        fetching: false,
        map: {},
        isPagesBulkDeleteRunning: false,
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { id, page }) {
            Vue.set(state.map, id, page);
        },
        deleteMapKey (state, { id }) {
            Vue.delete(state.map, id);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setIsPagesBulkDeleteRunning (state, isPagesBulkDeleteRunning) {
            state.isPagesBulkDeleteRunning = isPagesBulkDeleteRunning;
        },
    },
    actions: {
        async loadAll ({ commit }, { reFetch = false } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            commit('setFetching', { fetching: true });
            loadAllPromise = fetchPages();

            try {
                const { data: pages } = await loadAllPromise;
                const pagesWithKind = pages.map((page) => appendKindIfNotProvided(page, 'Page'));

                commit('setFetching', { fetching: false });
                commit('setMap', {
                    map: createFrozenMap(pagesWithKind),
                });
            } catch (error) {
                loadAllPromise = null;
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        loadOne ({ commit, getters }, { reFetch = false, ...payload }) {
            const { id } = payload;
            if (!reFetch && getters.pageById(id)) return Promise.resolve();

            commit('setFetching', { fetching: true });

            return fetchPage(id)
                .then(({ data: page }) => {
                    appendKindIfNotProvided(page, 'Page');

                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: page.id,
                        page: Object.freeze(page),
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        create ({ commit, dispatch }, { shouldStartProcessing = true, ...payload } = {}) {
            commit('setFetching', { fetching: true });

            return createPage(payload, shouldStartProcessing)
                .then(({ data: page }) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: page.id,
                        page: Object.freeze(page),
                    });
                    dispatch('pages/suggestedPages/fetchSubscriptionPageRules', {}, { root: true });

                    return page;
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        update ({ getters, commit, dispatch }, { shouldStartProcessing = false, ...page }) {
            const existingPage = getters.pageById(page.id);
            const existingPageWithNewRules = { ...existingPage, ...page };
            commit('setFetching', { fetching: true });

            return updatePage(existingPageWithNewRules, shouldStartProcessing)
                .then(({ data: savedPage }) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: savedPage.id,
                        page: Object.freeze(savedPage),
                    });
                    dispatch('pages/suggestedPages/fetchSubscriptionPageRules', {}, { root: true });

                    return savedPage;
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        remove ({ commit, dispatch }, payload) {
            commit('setFetching', { fetching: true });

            return deletePage(payload)
                .then(() => {
                    commit('setFetching', { fetching: false });
                    commit('deleteMapKey', { id: payload.id });
                    dispatch('pages/suggestedPages/fetchSubscriptionPageRules', {}, { root: true });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        async assignGroup ({ commit, dispatch }, payload) {
            const { page, group } = payload;

            const groupPayload = {
                groupId: group.id,
                itemId: page.id,
                itemType: 'page',
            };

            commit('setFetching', { fetching: true });

            try {
                await dispatch('groups/addItemToGroup', groupPayload, { root: true });

                const updatedPage = { ...page, group };

                commit('setMapAtKey', {
                    id: page.id,
                    page: Object.freeze(updatedPage),
                });

                return page;
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
    },
    getters: {
        list: (state) => values(state.map),
        pageById: (state) => (id) => state.map[id],
        pagesByAppId: (state, getters) => (appId) => getters.list.filter((page) => page.appId === appId),
        pagesByUrl: (state, getters) => (normalizedUrl) => {
            return getters.list.filter((page) => {
                return page.rules.some((rule) => {
                    const regex = new RegExp(rule.parsedRule);

                    return regex.test(normalizedUrl);
                });
            });
        },
        analyticsPagesListWithStars: (state, getters, _rootState, rootGetters) => {
            const isStarred = useStarsStore().getStarByItemId;

            return getters.list.map((page) =>
                Object.freeze({
                    id: page.id,
                    name: page.name,
                    group: page.group,
                    appId: page.appId,
                    appIds: page.appIds,
                    appIcon: rootGetters['apps/getIconByAppId'](page.appId),
                    kind: page.kind,
                    dirty: page.dirty,
                    isCoreEvent: page.isCoreEvent,
                    isStarred: !!isStarred(page.id),
                    icon: page.icon || { type: 'page' },
                    eventPropertyConfigurations: page.eventPropertyConfigurations,
                    eventPropertyNameList: page.eventPropertyNameList,
                }),
            );
        },
        recentPagesInReporting: (state, _getters, rootState, rootGetters) => {
            const isStarred = useStarsStore().getStarByItemId;

            return rootGetters['userSettings/history/itemHistory'](
                RECENTLY_SELECTED_HISTORY_TYPES.PAGE_RECENTLY_SELECTED,
            )
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => {
                    const page = state.map[id];

                    return {
                        id: page.id,
                        name: page.name,
                        group: page.group,
                        appId: page.appId,
                        appIds: page.appIds,
                        appIcon: rootGetters['apps/getIconByAppId'](page.appId),
                        appDisplayName: formatCrossAppDisplayName(page, rootState.apps.map),
                        kind: page.kind,
                        dirty: page.dirty,
                        isCoreEvent: page.isCoreEvent,
                        isStarred: !!isStarred(page.id),
                        icon: page.icon || { type: 'page' },
                        eventPropertyConfigurations: page.eventPropertyConfigurations,
                        eventPropertyNameList: page.eventPropertyNameList,
                        lastUsed: timestamp,
                    };
                });
        },
    },
};

export default pages;

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}
